import { NEW_CHAT_TITLE } from 'dragonmouse-shared/constants';
import { ChatRef, DsChat, Frame, SceneChat } from 'dragonmouse-shared/interfaces';
import { combineReducers } from 'redux';
import dbReducer, { selectActiveDsChat, selectActiveSceneChat, selectFrames } from './db/reducers';
import modeReducer from './mode/reducers';
import type { ChatTab } from './mode/util/chatTabs';

const rootReducer = combineReducers({
	db: dbReducer,
	mode: modeReducer,
});

export default rootReducer;

export type RootReducer = ReturnType<typeof rootReducer>;

//------------------------------------------------------------------------------------------------
// Derived state selectors that span multiple slices
//------------------------------------------------------------------------------------------------

// The active scene/DS chat, resolved from persisted state or, when the active id is an
// unsent client-only draft, from the draft itself — so the conversation renders whether
// or not the chat has been saved yet.
export const selectActiveSceneChatOrDraft = ({ db, mode }: RootReducer): SceneChat | null => {
	const chat = selectActiveSceneChat(db, mode.activeSceneChatId);
	if (chat) return chat;
	if (mode.draftSceneChat && mode.draftSceneChat.id === mode.activeSceneChatId) {
		return mode.draftSceneChat;
	}
	return null;
};

export const selectActiveDsChatOrDraft = ({ db, mode }: RootReducer): DsChat | null => {
	const chat = selectActiveDsChat(db, mode.activeDsChatId);
	if (chat) return chat;
	if (mode.draftDsChat && mode.draftDsChat.id === mode.activeDsChatId) {
		return mode.draftDsChat;
	}
	return null;
};

// Open chat tabs, resolved against the chats that actually exist. Tab ids are held
// loosely — a chat can be deleted from another browser tab, and a draft disappears
// when abandoned — so an id that resolves to nothing simply drops out of the strip.
// Memoized on its inputs: the result is compared by reference on every store change.
const buildChatTabs = (
	tabIds: string[],
	chatRefs: ChatRef[],
	draft: { id: string; title: string } | null
): ChatTab[] => {
	const titles = new Map<string, string>();
	for (const ref of chatRefs) {
		titles.set(ref.id, ref.title || 'Untitled Chat');
	}
	if (draft) {
		titles.set(draft.id, draft.title || NEW_CHAT_TITLE);
	}
	return tabIds
		.filter((id) => titles.has(id))
		.map((id) => ({
			id,
			title: titles.get(id) as string,
			isDraft: id === draft?.id,
		}));
};

let _prevSceneTabInputs: unknown[] = [];
let _cachedSceneTabs: ChatTab[] = [];

export const selectSceneChatTabs = ({ db, mode }: RootReducer): ChatTab[] => {
	const inputs = [mode.sceneChatTabs, db.sceneChatRefs, mode.draftSceneChat];
	if (inputs.every((input, i) => input === _prevSceneTabInputs[i])) return _cachedSceneTabs;
	_prevSceneTabInputs = inputs;
	_cachedSceneTabs = buildChatTabs(mode.sceneChatTabs, db.sceneChatRefs, mode.draftSceneChat);
	return _cachedSceneTabs;
};

let _prevDsTabInputs: unknown[] = [];
let _cachedDsTabs: ChatTab[] = [];

export const selectDsChatTabs = ({ db, mode }: RootReducer): ChatTab[] => {
	const inputs = [mode.dsChatTabs, db.dsChatRefs, mode.draftDsChat];
	if (inputs.every((input, i) => input === _prevDsTabInputs[i])) return _cachedDsTabs;
	_prevDsTabInputs = inputs;
	_cachedDsTabs = buildChatTabs(mode.dsChatTabs, db.dsChatRefs, mode.draftDsChat);
	return _cachedDsTabs;
};

export const selectSelectedFrames = (rootReducer: RootReducer) => {
	const frames = selectFrames(rootReducer.db);
	return rootReducer.mode.selected.reduce((selectedFrames, pick) => {
		if (pick.isFrame()) {
			const selectedFrame = frames.find((f) => f.id === pick.frameId);
			if (selectedFrame) selectedFrames.push(selectedFrame);
		}
		return selectedFrames;
	}, [] as Frame[]);
};

// New semantic loading selectors
export const selectIsAppReady = ({ db }: RootReducer): boolean => {
	// App is ready when user and scenes are loaded
	return db.loading.profile === 'loaded' && db.loading.scenes === 'loaded';
};

export const selectIsActiveSceneReady = ({ db }: RootReducer): boolean => {
	// Active scene is ready when frames are loaded (chats can still be loading)
	return db.loading.activeScene.frames === 'loaded';
};

// This will give a nice global loading state. We can make future ones that only
// care about certain subsets of the data.
export const selectIsLoadingAnything = ({ db, mode }: RootReducer): boolean => {
	// If the user is not logged in, we are loading
	// This works with the way we have studio set up to redirect away if the user
	// is signed out. So if this is null, that means we are waiting for user info
	// to be loaded.
	// ??? : do we need to check authUser here -- if we haven't loaded user, app won't be ready
	//       i am already doing a big change right now and am reluctant to push further with my cleanup
	//       so not digging in right now - ss
	if (!mode.authUser) return true;

	if (mode.activeSceneId) {
		if (db.sceneLoadError !== null) return false;

		// When a scene is active, we are loading until app is ready AND active scene is ready
		return !selectIsAppReady({ db, mode }) || !selectIsActiveSceneReady({ db, mode });
	} else {
		// When no scene is active, we are loading if app is not ready
		return !selectIsAppReady({ db, mode });
	}
};
